import { narrowAssetLanguage } from "@shared/vod-title";

/**
 * Audio and subtitle options, reconciled between two sources that disagree
 * (TV-5).
 *
 * ## THE RULE, said once
 *
 * **The manifest is the truth for playback; the catalogue labels are the truth
 * for the catalogue.** Those are different questions and they have different
 * right answers, so neither source "wins" outright:
 *
 * - The picker is built from the MANIFEST. Offering a guest a Swedish audio
 *   track that the packager never produced is a button that does nothing, and a
 *   button that does nothing on a television is indistinguishable from a broken
 *   television.
 * - The disagreement is a CATALOGUE finding, and it is reported by name
 *   ({@link reconcileLanguages} → `trackReport.ts`) rather than swallowed. A
 *   title whose `subtitleLanguages` promises Finnish that no packaging carries
 *   is wrong in the shop window, and nobody upstream will ever find that out
 *   from a player that silently rendered the shorter list.
 *
 * `spokenLanguage`, `audioLanguages[]` and `subtitleLanguages[]` are labels on a
 * **Media**, never on a channel — BE-4's correction. This module therefore takes
 * them as a plain bag of strings and asks nothing about where they came from.
 *
 * ## 🚨 Language matching is CASE-INSENSITIVE, and that is not a style choice
 *
 * Production holds **37 UPPERCASE language codes that staging does not**
 * ([[catalogue-data-quality]]). A `===` between a manifest's `"sv"` and a
 * catalogue's `"SV"` is green in every environment a developer looks at and
 * drops tracks in the only one that matters. Every comparison here goes through
 * `narrowAssetLanguage` from `@shared/vod-title`, which lowercases and drops the
 * region subtag a packager will happily emit (`sv-SE`, `pt-BR`, `zh-Hans`).
 *
 * ⚠️ And then **selection uses the manifest's OWN spelling**, not the
 * canonicalised one — see `rawLanguage` below. We match loosely and act
 * literally; handing a normalised code back to a player that indexed its tracks
 * by the original string is how the case problem comes back one layer down.
 */

/** Codes a packager writes when there is no language to name. Never a finding. */
const UNSPECIFIED = new Set(["und", "zxx", "mis", "mul"]);

/**
 * ISO 639-2 (three letters) folded onto ISO 639-1 (two), FOR MATCHING ONLY.
 *
 * The case problem has a twin that is just as invisible and produces a much
 * noisier failure: **DASH packagers routinely spell languages in alpha-3**
 * (`swe`, `eng`, `fin`) while the catalogue's `VarChar(3)` column holds the
 * alpha-2 the portal writes. Without a fold, every such title reports BOTH
 * halves of the disagreement at once - "Swedish promised and not packaged" and
 * "Swedish packaged and not promised", about the same track - and a report that
 * cries wolf on ordinary titles is a report an operator stops reading. That
 * costs more than having no report at all, which is the outcome this whole
 * mechanism exists to avoid.
 *
 * Both the bibliographic (639-2/B) and terminological (639-2/T) spellings are
 * listed where they differ (`ger`/`deu`, `fre`/`fra`, `dut`/`nld`) because a
 * packager may emit either and neither is more correct.
 *
 * ⚠️ **Matching only. Nothing is ever STORED through this table**, and an
 * unmapped three-letter code is left exactly as it is rather than guessed at -
 * a wrong fold would merge two languages into one row in the picker, which is
 * worse than the false finding it was trying to avoid.
 *
 * `nb` / `nn` / `no` are deliberately NOT folded into each other. The catalogue
 * holds all three as separate languages (`no` 73 rows, `nb` 12), an operator
 * chose between them, and collapsing them here would hide that choice.
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
  ara: "ar",
  ces: "cs",
  cze: "cs",
  dan: "da",
  deu: "de",
  ell: "el",
  eng: "en",
  est: "et",
  fao: "fo",
  fas: "fa",
  fin: "fi",
  fra: "fr",
  fre: "fr",
  ger: "de",
  gre: "el",
  heb: "he",
  hin: "hi",
  hun: "hu",
  isl: "is",
  ice: "is",
  ita: "it",
  jpn: "ja",
  kor: "ko",
  lav: "lv",
  lit: "lt",
  dut: "nl",
  nld: "nl",
  nno: "nn",
  nob: "nb",
  nor: "no",
  per: "fa",
  pol: "pl",
  por: "pt",
  ron: "ro",
  rum: "ro",
  rus: "ru",
  slk: "sk",
  slo: "sk",
  spa: "es",
  swe: "sv",
  tur: "tr",
  ukr: "uk",
  zho: "zh",
  chi: "zh",
};

/**
 * The one spelling two sources are compared in: lowercase, region dropped,
 * alpha-3 folded. Null for anything that is not a language.
 */
function matchLanguage(value: unknown): string | null {
  const narrowed = narrowAssetLanguage(value);
  if (!narrowed) return null;
  const code = ALPHA3_TO_ALPHA2[narrowed.code] ?? narrowed.code;
  return UNSPECIFIED.has(code) ? null : code;
}

/** One audio or text rendition, as the manifest describes it. */
export interface ManifestTrack {
  /** The player's own handle for this track. Opaque to us. */
  id: number;
  /** Exactly as the manifest spelled it — `"sv"`, `"SV"`, `"sv-SE"`, `"und"`. */
  language: string;
  /** The packager's own display label, when it wrote one. */
  label?: string | null;
  /** DASH/HLS roles: `main`, `dub`, `commentary`, `caption`, `subtitle`… */
  roles?: readonly string[];
  /** Text tracks only: `subtitle` or `caption` (hard-of-hearing). */
  kind?: string | null;
  channelsCount?: number | null;
  active?: boolean;
}

/** What the CATALOGUE says this title has, off the Media's labels. */
export interface CatalogueLanguages {
  /** Only used to name the finding; never logged when absent. */
  assetId?: string | null;
  spokenLanguage?: string | null;
  audioLanguages?: readonly string[] | null;
  subtitleLanguages?: readonly string[] | null;
}

export type TrackKind = "audio" | "text";

/** One row in the picker. Exactly one per thing a guest can choose. */
export interface TrackOption {
  kind: TrackKind;
  /** Stable within a load. Used as the React key and the test id. */
  value: string;
  /** Canonical lowercase code, or null for an unnamed language. */
  language: string | null;
  /**
   * The manifest's own spelling, handed straight back to the player on select.
   * See the case-insensitivity note above.
   */
  rawLanguage: string;
  /** What a guest reads, ten feet away. */
  label: string;
  /** Commentary, described audio, hard-of-hearing — worth saying out loud. */
  note: string | null;
  ids: number[];
  active: boolean;
}

/**
 * Human names, from the platform's own locale data where it has any.
 *
 * `Intl.DisplayNames` is present on every Android WebView we ship to and is NOT
 * guaranteed on older Tizen/webOS builds, so it is probed and never assumed —
 * the same capability-not-platform rule the DRM check follows. The fallback is
 * the manifest's label, then the bare code in upper case, which at least reads
 * as a language rather than as a bug.
 */
function displayNames(
  locale: string
): { of(code: string): string | undefined } | null {
  try {
    const Ctor = (
      Intl as unknown as {
        DisplayNames?: new (
          locales: string[],
          options: { type: string }
        ) => { of(code: string): string | undefined };
      }
    ).DisplayNames;
    if (typeof Ctor !== "function") return null;
    return new Ctor([locale], { type: "language" });
  } catch {
    return null;
  }
}

function titleCase(text: string): string {
  return text.length === 0 ? text : text[0]!.toUpperCase() + text.slice(1);
}

/** The note that goes beside a name, when the track is not the plain one. */
function roleNote(track: ManifestTrack): string | null {
  const roles = (track.roles ?? []).map((r) => r.toLowerCase());
  if (roles.includes("commentary")) return "Commentary";
  if (roles.includes("description")) return "Audio description";
  if (track.kind === "caption" || roles.includes("caption")) {
    return "For the hard of hearing";
  }
  if (roles.includes("dub")) return "Dubbed";
  return null;
}

function optionLabel(
  track: ManifestTrack,
  code: string | null,
  names: { of(code: string): string | undefined } | null
): string {
  const fromLocale = code ? names?.of(code) : undefined;
  if (fromLocale && fromLocale.toLowerCase() !== code)
    return titleCase(fromLocale);
  const label = track.label?.trim();
  if (label) return label;
  if (code) return code.toUpperCase();
  return "Original";
}

/**
 * Collapse the manifest's renditions into the list a guest chooses from.
 *
 * A DASH manifest carries one variant per bitrate per language, so a three-rung
 * ABR ladder in two languages is six variants and **two** options. Grouping is
 * by (language, note): a Swedish commentary track and Swedish dialogue are two
 * genuinely different choices, while 480p-Swedish and 1080p-Swedish are one.
 */
export function trackOptions(
  tracks: readonly ManifestTrack[],
  kind: TrackKind,
  locale = "en"
): TrackOption[] {
  const names = displayNames(locale);
  const byKey = new Map<string, TrackOption>();
  for (const track of tracks) {
    const raw = typeof track.language === "string" ? track.language.trim() : "";
    const code = matchLanguage(raw);
    const note = roleNote(track);
    const key = `${code ?? `raw:${raw.toLowerCase()}`}|${note ?? ""}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.ids.push(track.id);
      existing.active = existing.active || track.active === true;
      continue;
    }
    byKey.set(key, {
      kind,
      value: key,
      language: code,
      rawLanguage: raw,
      label: optionLabel(track, code, names),
      note,
      ids: [track.id],
      active: track.active === true,
    });
  }
  // Named languages first, alphabetically by what the guest reads; unnamed and
  // commentary tracks last. Manifest order is an accident of packaging and
  // would reshuffle a menu between two loads of the same title.
  return [...byKey.values()].sort((a, b) => {
    if (!!a.note !== !!b.note) return a.note ? 1 : -1;
    if (!!a.language !== !!b.language) return a.language ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
}

/* -------------------------------------------------------------------------- */
/* The disagreement                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One way the catalogue's labels and the manifest's tracks fail to agree.
 *
 * Named per direction, because the two directions want different people:
 * `*_promised_not_packaged` is a **shop-window lie** — a guest was told this
 * title has Finnish subtitles and it does not — and belongs to whoever curates
 * the catalogue. `*_packaged_not_promised` is a **missing label** — the stream
 * carries Danish audio nobody advertised — and costs a title its place in a
 * language filter. Neither is a playback failure and neither is ever counted as
 * one; conflating them with `StreamFailure` would page the fleet console for a
 * metadata typo.
 */
export type TrackDisagreement =
  | "audio_promised_not_packaged"
  | "audio_packaged_not_promised"
  | "subtitle_promised_not_packaged"
  | "subtitle_packaged_not_promised"
  | "spoken_language_not_packaged";

export interface LanguageDisagreement {
  code: TrackDisagreement;
  /** The canonical language codes involved, so a finding is actionable. */
  languages: string[];
}

function canonicalSet(
  values: readonly (string | null | undefined)[]
): Set<string> {
  const out = new Set<string>();
  for (const value of values) {
    const code = matchLanguage(value);
    if (code) out.add(code);
  }
  return out;
}

function optionLanguages(options: readonly TrackOption[]): Set<string> {
  return canonicalSet(options.map((o) => o.language));
}

function missing(from: Set<string>, inside: Set<string>): string[] {
  return [...from].filter((code) => !inside.has(code)).sort();
}

/**
 * Everything the two sources disagree about, as named codes.
 *
 * Returns an empty array for the overwhelmingly common case, so a caller can
 * treat "anything at all" as the thing worth reporting. Nothing here decides
 * what to DO about a disagreement — that is `trackReport.ts`, deliberately
 * separate, so this half stays a pure function that a test can enumerate.
 */
export function reconcileLanguages(
  audio: readonly TrackOption[],
  text: readonly TrackOption[],
  catalogue: CatalogueLanguages | null | undefined
): LanguageDisagreement[] {
  if (!catalogue) return [];
  const out: LanguageDisagreement[] = [];

  const audioPackaged = optionLanguages(audio);
  const textPackaged = optionLanguages(text);
  const audioPromised = canonicalSet(catalogue.audioLanguages ?? []);
  const textPromised = canonicalSet(catalogue.subtitleLanguages ?? []);

  const add = (code: TrackDisagreement, languages: string[]) => {
    if (languages.length > 0) out.push({ code, languages });
  };

  // Only compared when the catalogue said something. A title with no
  // `audioLanguages` at all is unlabelled, not wrong, and reporting every
  // unlabelled title would bury the ones that are actually contradicted.
  if (audioPromised.size > 0) {
    add("audio_promised_not_packaged", missing(audioPromised, audioPackaged));
    add("audio_packaged_not_promised", missing(audioPackaged, audioPromised));
  }
  if (textPromised.size > 0) {
    add("subtitle_promised_not_packaged", missing(textPromised, textPackaged));
    add("subtitle_packaged_not_promised", missing(textPackaged, textPromised));
  }

  const spoken = canonicalSet([catalogue.spokenLanguage]);
  if (spoken.size > 0 && audioPackaged.size > 0) {
    add("spoken_language_not_packaged", missing(spoken, audioPackaged));
  }
  return out;
}

/** The one-line, operator-readable form of a disagreement. */
export function describeDisagreement(d: LanguageDisagreement): string {
  return `${d.code}=${d.languages.join(",")}`;
}
